import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { ArrowDownCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WithdrawalRequestModal({ isOpen, onClose, portfolioValue, onSuccess }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    request_type: '',
    amount: '',
    notes: '',
    bank_account: ''
  });

  const requestTypes = [
    { value: 'interest_distribution', label: 'Interest Distribution', description: 'Request monthly interest payout' },
    { value: 'partial_redemption', label: 'Partial Redemption', description: 'Withdraw part of your investment' },
    { value: 'full_redemption', label: 'Full Redemption', description: 'Withdraw entire investment' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.request_type) {
      toast.error('Please select a request type');
      return;
    }

    if (formData.request_type === 'partial_redemption' && (!formData.amount || parseFloat(formData.amount) <= 0)) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (formData.request_type === 'partial_redemption' && parseFloat(formData.amount) > portfolioValue) {
      toast.error(`Amount cannot exceed available balance of R${portfolioValue?.toLocaleString()}`);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        request_type: formData.request_type,
        amount: formData.request_type === 'partial_redemption' ? parseFloat(formData.amount) : null,
        notes: formData.notes || null,
        bank_account: formData.bank_account || null
      };

      await axios.post(`${API_URL}/api/investor/withdrawal-requests`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Withdrawal request submitted successfully');
      onSuccess?.();
      onClose();
      setFormData({ request_type: '', amount: '', notes: '', bank_account: '' });
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-[#1a1a1a] border-[#2a2a2a] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ArrowDownCircle className="h-5 w-5 text-[#B8860B]" />
            Request Withdrawal
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Submit a request to withdraw funds from your investment portfolio.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Request Type</Label>
            <Select 
              value={formData.request_type} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, request_type: value }))}
            >
              <SelectTrigger className="bg-[#2a2a2a] border-[#3a3a3a]">
                <SelectValue placeholder="Select request type" />
              </SelectTrigger>
              <SelectContent className="bg-[#2a2a2a] border-[#3a3a3a]">
                {requestTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div>
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-gray-400">{type.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {formData.request_type === 'partial_redemption' && (
            <div className="space-y-2">
              <Label>Amount (ZAR)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={portfolioValue}
                placeholder="Enter amount"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                className="bg-[#2a2a2a] border-[#3a3a3a]"
              />
              <p className="text-xs text-gray-400">
                Available balance: R{portfolioValue?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
          )}

          {formData.request_type === 'full_redemption' && (
            <div className="p-3 bg-[#2a2a2a] rounded-lg">
              <p className="text-sm text-gray-300">
                Full redemption amount: <span className="text-[#B8860B] font-semibold">
                  R{portfolioValue?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>Bank Account (Optional)</Label>
            <Input
              placeholder="Enter bank account details for payment"
              value={formData.bank_account}
              onChange={(e) => setFormData(prev => ({ ...prev, bank_account: e.target.value }))}
              className="bg-[#2a2a2a] border-[#3a3a3a]"
            />
          </div>

          <div className="space-y-2">
            <Label>Additional Notes (Optional)</Label>
            <Textarea
              placeholder="Any additional information or special instructions"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="bg-[#2a2a2a] border-[#3a3a3a] min-h-[80px]"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="border-[#3a3a3a] hover:bg-[#2a2a2a]"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-[#B8860B] hover:bg-[#9A7209] text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
